import React, { FC, CSSProperties, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dropdownStyle } from '../components/StyledSelect';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../utils/api';
import DateTimePickerModal from '../components/DateTimePickerModal';

interface IdName { id: string; name: string }
interface WorkShiftRef { id: string; shift_number: number; shift_start?: string; shift_end?: string }
interface DrillerRef { id: string; name: string; is_working?: boolean }
interface EquipmentRef { id: string; name: string; type_name?: string; serial_number?: string }
interface WorkKindRef { id: string; name: string; category_id?: string }
interface WorkCategoryRef { id: string; name: string; npt_type?: string }

interface StatisticsResponse {
  code: number;
  message: string;
  main_refs?: {
    contractor_list?: IdName[];
    deposit_list?: IdName[];
    mine_list?: IdName[];
    incline_list?: IdName[];
    station_list?: IdName[];
  };
  equipment?: {
    telemetry_system_list?: EquipmentRef[];
    pdm_list?: EquipmentRef[];
    drill_bit_list?: EquipmentRef[];
  };
  crew?: {
    work_shift_list?: WorkShiftRef[];
    driller_list?: DrillerRef[];
  };
  well_list?: IdName[];
  work_kind_list?: WorkKindRef[];
  work_category_list?: WorkCategoryRef[];
}

interface CrewRow { shift: string; drillers: string[] }
interface TimeRow { workType?: string; start?: string; end?: string; duration?: string }
interface DepthRow { workType?: string; startDepth?: string; endDepth?: string; meters?: string }
interface WellSection {
  wellId?: string;
  timeRows: TimeRow[];
  depthRows: DepthRow[];
}

// helpers
const onlyDigitsLen = (v: string, maxLen: number) => (v.replace(/\D+/g, '').slice(0, maxLen));

const AddReportPage: FC = () => {
  const { user } = useAuth();
  const token = user?.access_token;
  const navigate = useNavigate();
  const [openDate, setOpenDate] = useState(false);
  const [reportDate, setReportDate] = useState<Date>(new Date());

  const [contractors, setContractors] = useState<IdName[]>([]);
  const [deposits, setDeposits] = useState<IdName[]>([]);
  const [mines, setMines] = useState<IdName[]>([]);
  const [inclines, setInclines] = useState<IdName[]>([]);
  const [wells, setWells] = useState<IdName[]>([]);
  const [stations, setStations] = useState<IdName[]>([]);
  const [telemetrySystems, setTelemetrySystems] = useState<IdName[]>([]);
  const [pdms, setPdms] = useState<IdName[]>([]);
  const [drillBits, setDrillBits] = useState<IdName[]>([]);
  const [workShifts, setWorkShifts] = useState<WorkShiftRef[]>([]);
  const [drillers, setDrillers] = useState<DrillerRef[]>([]);
  const [workKinds, setWorkKinds] = useState<WorkKindRef[]>([]);
  const [workCategories, setWorkCategories] = useState<WorkCategoryRef[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');
  // Equipment selections
  const [selectedTele, setSelectedTele] = useState<string>('');
  const [selectedVzd, setSelectedVzd] = useState<string>('');
  const [selectedBit, setSelectedBit] = useState<string>('');

  // Top-level selections
  const [selectedCompany, setSelectedCompany] = useState<string>(''); // placeholder
  const [selectedDeposit, setSelectedDeposit] = useState<string>('');
  const [selectedMine, setSelectedMine] = useState<string>('');
  const [selectedIncline, setSelectedIncline] = useState<string>('');
  const [selectedStation, setSelectedStation] = useState<string>('');

  // Crew composition
  const [crewRows, setCrewRows] = useState<CrewRow[]>([{ shift: '1', drillers: ['', ''] }]);

  // Well sections
  const newWellSection = (): WellSection => ({
    wellId: wells[0]?.id,
    timeRows: Array.from({ length: 5 }, () => ({ workType: '001A', start: '', end: '', duration: '120' })),
    depthRows: Array.from({ length: 5 }, () => ({ workType: 'Бурение', startDepth: '', endDepth: '', meters: '' }))
  });
  const [sections, setSections] = useState<WellSection[]>([newWellSection()]);

  useEffect(() => {
    if (!token) return;
    const fetchLists = async () => {
      try {
        const res = await fetch(apiUrl('/statistics/reports/params'), {
          method: 'GET',
          headers: { 'Accept': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        });
        if (!res.ok) throw new Error(`statistics fetch failed: ${res.status}`);
        const data: StatisticsResponse = await res.json();
        setContractors(data.main_refs?.contractor_list || []);
        setDeposits(data.main_refs?.deposit_list || []);
        setMines(data.main_refs?.mine_list || []);
        setInclines(data.main_refs?.incline_list || []);
        setWells(data.well_list || []);
        setStations(data.main_refs?.station_list || []);
        setTelemetrySystems(data.equipment?.telemetry_system_list?.map(e => ({ id: e.id, name: e.name })) || []);
        setPdms(data.equipment?.pdm_list?.map(e => ({ id: e.id, name: e.name })) || []);
        setDrillBits(data.equipment?.drill_bit_list?.map(e => ({ id: e.id, name: e.name })) || []);
        setWorkShifts(data.crew?.work_shift_list || []);
        setDrillers(data.crew?.driller_list || []);
        setWorkKinds(data.work_kind_list || []);
        setWorkCategories(data.work_category_list || []);
        setSubmitError('');
      } catch (e) { console.error(e); }
    };
    fetchLists();
  }, [token]);

  const fmtDate = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2,'0');
    return `${pad(d.getDate())}.${pad(d.getMonth()+1)}.${String(d.getFullYear()).slice(-2)}`;
  };
  const fmtApiDate = (d: Date) => d.toISOString().slice(0, 10);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');

    const reportBody = {
      report_date: fmtApiDate(reportDate),
      main_refs: {
        contractor_id: selectedCompany || undefined,
        deposit_id: selectedDeposit || undefined,
        mine_id: selectedMine || undefined,
        incline_id: selectedIncline || undefined,
        station_id: selectedStation || undefined,
      },
      equipment: {
        telemetry_system_id: selectedTele || undefined,
        pdm_id: selectedVzd || undefined,
        drill_bit_id: selectedBit || undefined,
      },
      crew: {
        shift_id: crewRows[0]?.shift || undefined,
        driller_id_list: crewRows[0]?.drillers.filter(Boolean) || [],
      },
      wells_stats_list: sections
        .filter(section => section.wellId)
        .map(section => ({
          id: section.wellId,
          stats_by_time_list: section.timeRows
            .filter(row => row.workType || row.start || row.end)
            .map(row => ({
              work_kind_id: row.workType || undefined,
              time_start: Number(row.start) || 0,
              time_end: Number(row.end) || 0,
            })),
          stats_by_depth_list: section.depthRows
            .filter(row => row.workType || row.startDepth || row.endDepth)
            .map(row => ({
              work_category_id: row.workType || undefined,
              depth_start: row.startDepth || '',
              depth_end: row.endDepth || '',
            })),
        }))
        .filter(section => section.stats_by_time_list.length || section.stats_by_depth_list.length),
    };

    try {
      const response = await fetch(apiUrl('/statistics/reports'), {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(reportBody),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.code !== undefined && result.code !== 0) {
        const message = result?.message || `Ошибка отправки отчёта: ${response.status}`;
        throw new Error(message);
      }

      navigate('/statistics');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Ошибка отправки отчёта');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const addDriller = (rowIdx: number) => setCrewRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, drillers: [...r.drillers, ''] } : r));
  const removeDriller = (rowIdx: number, drillerIdx: number) => setCrewRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, drillers: r.drillers.filter((_, di) => di !== drillerIdx) } : r));
  const updateDriller = (rowIdx: number, drillerIdx: number, value: string) => setCrewRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, drillers: r.drillers.map((d, di) => di === drillerIdx ? value : d) } : r));

  const addSection = () => setSections(prev => [...prev, newWellSection()]);
  const removeSection = (i: number) => setSections(prev => prev.filter((_, idx) => idx !== i));

  const updateSection = (i: number, patch: Partial<WellSection>) => {
    setSections(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  };

  const updateTimeRow = (si: number, ri: number, patch: Partial<TimeRow>) => {
    setSections(prev => prev.map((s, idx) => idx === si ? { ...s, timeRows: s.timeRows.map((r, ridx) => ridx===ri ? { ...r, ...patch } : r) } : s));
  };
  const addTimeRow = (si: number) => updateSection(si, { timeRows: [...sections[si].timeRows, { workType: '001A', start: '', end: '', duration: '120' }] });
  const removeTimeRow = (si: number, ri: number) => updateSection(si, { timeRows: sections[si].timeRows.filter((_, idx) => idx !== ri) });

  const updateDepthRow = (si: number, ri: number, patch: Partial<DepthRow>) => {
    setSections(prev => prev.map((s, idx) => idx === si ? { ...s, depthRows: s.depthRows.map((r, ridx) => ridx===ri ? { ...r, ...patch } : r) } : s));
  };
  const addDepthRow = (si: number) => updateSection(si, { depthRows: [...sections[si].depthRows, {}] });
  const removeDepthRow = (si: number, ri: number) => updateSection(si, { depthRows: sections[si].depthRows.filter((_, idx) => idx !== ri) });

  return (
    <div style={styles.container}>
      {/* Top bar: title and date */}
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 18 }}>Отчет оператора буровой ООО «ГЕОДАРТ»</span>
          <button onClick={()=>setOpenDate(true)} style={styles.topDateBtn}>
            <span>{fmtDate(reportDate)}</span>
            <span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="3" width="12" height="11" rx="2" stroke="#888" strokeWidth="1"/>
                <path d="M5 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                <path d="M11 1V4" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
                <path d="M3 6H13" stroke="#888" strokeWidth="1" strokeLinecap="round"/>
              </svg>
            </span>
          </button>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button style={styles.secondaryBtn} onClick={()=>navigate('/statistics')}>Отмена</button>
          <button
            style={{
              ...styles.primaryBtn,
              opacity: submitting ? 0.6 : 1,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Сохранение...' : 'Сохранить'}
          </button>
          {submitError && <div style={styles.submitError}>{submitError}</div>}
        </div>
      </div>

      {/* Top info: two cards side by side */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap:12 }}>
        {/* Основное */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Основное</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5, minmax(0, 1fr))', gap:12 }}>
            <div>
              <div style={styles.label}>Компания</div>
              <select style={{ ...dropdownStyle, width:'100%', height:28 }} value={selectedCompany} onChange={e=>setSelectedCompany(e.target.value)}>
                <option value="">Выбрать</option>
                {contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <div style={styles.label}>Месторождение</div>
              <select style={{ ...dropdownStyle, width:'100%', height:28 }} value={selectedDeposit} onChange={e=>setSelectedDeposit(e.target.value)}>
                <option value="">Выбрать</option>
                {deposits.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <div style={styles.label}>Шахта</div>
              <select style={{ ...dropdownStyle, width:'100%', height:28 }} value={selectedMine} onChange={e=>setSelectedMine(e.target.value)}>
                <option value="">Выбрать</option>
                {mines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <div style={styles.label}>Уклон</div>
              <select style={{ ...dropdownStyle, width:'100%', height:28 }} value={selectedIncline} onChange={e=>setSelectedIncline(e.target.value)}>
                <option value="">Выбрать</option>
                {inclines.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
            <div>
              <div style={styles.label}>Буровая</div>
              <select style={{ ...dropdownStyle, width:'100%', height:28 }} value={selectedStation} onChange={e=>setSelectedStation(e.target.value)}>
                <option value="">Выбрать</option>
                {stations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
        </div>
        {/* Оборудование */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Оборудование</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3, minmax(0, 1fr))', gap:12 }}>
            <div>
              <div style={styles.label}>Телесистема</div>
              <select style={{ ...dropdownStyle, width:'100%', height:28 }} value={selectedTele} onChange={e=>setSelectedTele(e.target.value)}>
                <option value="">Выбрать</option>
                {telemetrySystems.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <div style={styles.label}>ВЗД</div>
              <select style={{ ...dropdownStyle, width:'100%', height:28 }} value={selectedVzd} onChange={e=>setSelectedVzd(e.target.value)}>
                <option value="">Выбрать</option>
                {pdms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <div style={styles.label}>Долото</div>
              <select style={{ ...dropdownStyle, width:'100%', height:28 }} value={selectedBit} onChange={e=>setSelectedBit(e.target.value)}>
                <option value="">Выбрать</option>
                {drillBits.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Состав бригады */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Состав бригады</div>
        {/* Labels row */}
        <div style={{ display:'flex', gap:8, marginBottom:4 }}>
          <div style={{ width:60 }}><span style={styles.label}>Смена:</span></div>
          <div style={{ width:140 }}><span style={styles.label}>Бурильщик:</span></div>
          <div style={{ width:140 }}><span style={styles.label}>Бурильщик:</span></div>
        </div>
        {crewRows.map((row, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <select style={{ ...dropdownStyle, height:28, width:100 }} value={row.shift} onChange={e=>{
              const v = (e.target as HTMLSelectElement).value; setCrewRows(prev => prev.map((r, idx)=> idx===i ? { ...r, shift: v } : r));
            }}>
              <option value="">Выбрать</option>
              {workShifts.map(s => (
                <option key={s.id} value={s.id}>{s.shift_number ? `Смена ${s.shift_number}` : s.id}</option>
              ))}
            </select>
            {row.drillers.map((driller, di) => (
              <div key={di} style={{ display:'flex', alignItems:'center', gap:4 }}>
                <select style={{ ...dropdownStyle, height:28, width:140 }} value={driller} onChange={e=>updateDriller(i, di, e.target.value)}>
                  <option value="">Выбрать</option>
                  {drillers.map(dr => <option key={dr.id} value={dr.id}>{dr.name}</option>)}
                </select>
                <button onClick={()=>removeDriller(i, di)} style={styles.deleteIconBtn} disabled={row.drillers.length <= 1}>×</button>
              </div>
            ))}
            <button onClick={()=>addDriller(i)} style={styles.addDrillerBtn}>+</button>
          </div>
        ))}
      </div>

      {/* Well sections */}
      {sections.map((sec, si) => (
        <div key={si} style={styles.card}>
          {/* Header row: Well number and Drilling meterage */}
          <div style={{ display:'flex', gap:24, marginBottom:16 }}>
            {/* Left: Well number */}
            <div>
              <div style={styles.label}>№ Скважины</div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <select style={{ ...dropdownStyle, height:28, width:120 }} value={sec.wellId} onChange={e=>updateSection(si, { wellId: (e.target as HTMLSelectElement).value })}>
                  <option value="">001A</option>
                  {wells.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                <button
                  onClick={()=>removeSection(si)}
                  disabled={sections.length <= 1}
                  style={{
                    ...styles.deleteIconBtn,
                    ...(sections.length <= 1 ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                  }}
                >×</button>
              </div>
            </div>
            {/* Right: Drilling meterage */}
            <div style={{ marginLeft:'auto', display:'flex', gap:24, alignItems:'flex-start' }}>
              <div style={{ display:'flex', flexDirection:'column' }}>
                <div style={{ fontSize:13, color:'#bbb', marginBottom:2 }}>Метраж бурения</div>
                <div style={{ fontSize:11, color:'#666', maxWidth:200, lineHeight:1.3 }}>Будет рассчитано автоматически после заполнения формы по метражу.</div>
              </div>
              <div style={{ display:'flex', gap:16, alignItems:'flex-start' }}>
                <div>
                  <div style={styles.label}>Уголь</div>
                  <input style={{...styles.input, width:90, height:28}} readOnly />
                </div>
                <div>
                  <div style={styles.label}>Порода</div>
                  <input style={{...styles.input, width:90, height:28}} readOnly />
                </div>
                <div>
                  <div style={styles.label}>Общий</div>
                  <input style={{...styles.input, width:90, height:28}} readOnly />
                </div>
              </div>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)', gap:24 }}>
            {/* По времени */}
            <div>
              <div style={styles.tableDivider}>
                <span style={styles.dividerLine}></span>
                <span style={styles.dividerText}>По времени</span>
                <span style={styles.dividerLine}></span>
              </div>
              <div style={styles.tableHeader}>
                <div style={{...styles.tableHeaderCell, flex:1}}>Вид работы</div>
                <div style={{...styles.tableHeaderCell, flex:1}}>Начало</div>
                <div style={{...styles.tableHeaderCell, flex:1}}>Конец</div>
                <div style={{...styles.tableHeaderCell, flex:1}}>Продолжительность</div>
                <div style={{width:32}}></div>
              </div>
              {sec.timeRows.map((row, ri) => (
                <div key={ri} style={styles.tableRow}>
                  <select style={{ ...dropdownStyle, height:28, flex:1 }} value={row.workType || ''} onChange={e=>updateTimeRow(si, ri, { workType: (e.target as HTMLSelectElement).value })}>
                    <option value="">Выбрать</option>
                    {workKinds.length ? workKinds.map(kind => <option key={kind.id} value={kind.id}>{kind.name}</option>) : (
                      <>
                        <option value="001A">001A</option>
                        <option value="001B">001B</option>
                      </>
                    )}
                  </select>
                  <input
                    style={{...styles.tableInput, flex:1}}
                    inputMode="numeric"
                    value={row.start || ''}
                    onChange={e=>updateTimeRow(si, ri, { start: onlyDigitsLen(e.target.value, 3) })}
                  />
                  <input
                    style={{...styles.tableInput, flex:1}}
                    inputMode="numeric"
                    value={row.end || ''}
                    onChange={e=>updateTimeRow(si, ri, { end: onlyDigitsLen(e.target.value, 3) })}
                  />
                  <input
                    style={{...styles.tableInput, flex:1, textAlign:'center'}}
                    inputMode="numeric"
                    value={row.duration || ''}
                    onChange={e=>updateTimeRow(si, ri, { duration: onlyDigitsLen(e.target.value, 4) })}
                  />
                  <button style={styles.tableDeleteBtn} onClick={()=>removeTimeRow(si, ri)}>×</button>
                </div>
              ))}
              <button onClick={()=>addTimeRow(si)} style={styles.addRowBar}>+</button>
            </div>

            {/* По метражу */}
            <div>
              <div style={styles.tableDivider}>
                <span style={styles.dividerLine}></span>
                <span style={styles.dividerText}>По метражу</span>
                <span style={styles.dividerLine}></span>
              </div>
              <div style={styles.tableHeader}>
                <div style={{...styles.tableHeaderCell, flex:1}}>Тип работ</div>
                <div style={{...styles.tableHeaderCell, flex:1}}>Старт. глубина</div>
                <div style={{...styles.tableHeaderCell, flex:1}}>Конеч. глубина</div>
                <div style={{...styles.tableHeaderCell, flex:1}}>Метраж</div>
                <div style={{width:32}}></div>
              </div>
              {sec.depthRows.map((row, ri) => (
                <div key={ri} style={styles.tableRow}>
                  <select style={{ ...dropdownStyle, height:28, flex:1 }} value={row.workType || ''} onChange={e=>updateDepthRow(si, ri, { workType: (e.target as HTMLSelectElement).value })}>
                    <option value="">Выбрать</option>
                    {workCategories.length ? workCategories.map(kind => <option key={kind.id} value={kind.id}>{kind.name}</option>) : (
                      <>
                        <option value="Бурение">Бурение</option>
                        <option value="Монтаж">Монтаж</option>
                      </>
                    )}
                  </select>
                  <input
                    style={{...styles.tableInput, flex:1}}
                    inputMode="numeric"
                    value={row.startDepth || ''}
                    onChange={e=>updateDepthRow(si, ri, { startDepth: onlyDigitsLen(e.target.value, 3) })}
                  />
                  <input
                    style={{...styles.tableInput, flex:1}}
                    inputMode="numeric"
                    value={row.endDepth || ''}
                    onChange={e=>updateDepthRow(si, ri, { endDepth: onlyDigitsLen(e.target.value, 3) })}
                  />
                  <input
                    style={{...styles.tableInput, flex:1}}
                    inputMode="numeric"
                    value={row.meters || ''}
                    onChange={e=>updateDepthRow(si, ri, { meters: onlyDigitsLen(e.target.value, 4) })}
                  />
                  <button style={styles.tableDeleteBtn} onClick={()=>removeDepthRow(si, ri)}>×</button>
                </div>
              ))}
              <button onClick={()=>addDepthRow(si)} style={styles.addRowBar}>+</button>
            </div>
          </div>
        </div>
      ))}

      {/* Add section button */}
      <div style={{ display:'flex', justifyContent:'flex-start' }}>
        <button onClick={addSection} style={styles.addSectionBtn}>Добавить скважину</button>
      </div>

      <DateTimePickerModal
        isOpen={openDate}
        initialDate={reportDate}
        onClose={()=>setOpenDate(false)}
        onSelect={(d)=>{ setReportDate(d); setOpenDate(false); }}
        label="Дата отчета"
      />
    </div>
  );
};

const styles: { [key:string]: CSSProperties } = {
  container: { display:'flex', flexDirection:'column', gap:12, color:'#fff', boxSizing:'border-box', overflowX:'hidden' },
  pageHeader: {
    display:'flex', alignItems:'center', justifyContent:'space-between',
    background:'#1F2534', border:'1px solid #2E3749', boxShadow:'0 2px 16px #0000003D', borderRadius:4, padding:12,
    marginTop: 16
  },
  topDateBtn: { background:'#424F67', color:'#fff', border:'1px solid #2F3136', borderRadius:4, height:28, padding:'0 10px', display:'flex', alignItems:'center', gap:8 },
  secondaryBtn: { background:'#333B4E', color:'#fff', border:'1px solid #2E3749', borderRadius:4, height:28, padding:'0 12px', cursor:'pointer' },
  primaryBtn: { background:'#0066FF', color:'#fff', border:'none', borderRadius:4, height:28, padding:'0 12px', cursor:'pointer' },

  card: { background:'#1F2534', border:'1px solid #2E3749', boxShadow:'0 2px 16px #0000003D', borderRadius:4, padding:12 },
  cardTitle: { fontSize:16, marginBottom:8, color:'#bbb' },
  subCard: { background:'#1F2534', border:'1px solid #2E3749', borderRadius:8, padding:12 },
  subCardTitle: { fontSize:14, marginBottom:8, color:'#bbb', textAlign:'center', borderBottom:'1px solid #2E3749', paddingBottom:8 },
  label: { fontSize:12, color:'#808080', marginBottom:4 },
  subLabel: { fontSize:12, color:'#808080' },
  submitError: { color: '#FF8A8A', fontSize: 12, marginLeft: 12, maxWidth: 320 },
  input: { background:'#424F67', color:'#fff', border:'1px solid #2F3136', borderRadius:4, height:28, padding:'0 8px' },
  addRowBtn: { background:'#2E3749', color:'#fff', border:'1px solid #2E3749', borderRadius:4, height:28, width:28, cursor:'pointer', marginTop:8 },
  addDrillerBtn: { background:'transparent', color:'#51A0FF', border:'1px solid #51A0FF', borderRadius:4, height:28, width:28, cursor:'pointer', fontSize:18, display:'flex', alignItems:'center', justifyContent:'center' },
  deleteIconBtn: { background:'#2E3749', color:'#fff', border:'1px solid #2E3749', borderRadius:4, height:28, width:28, cursor:'pointer' },
  deleteSectionBtn: { background:'#2E3749', color:'#fff', border:'1px solid #2E3749', borderRadius:4, height:28, padding:'0 12px', cursor:'pointer' },
  addSectionBtn: { background:'#2E3749', color:'#fff', border:'1px solid #2E3749', borderRadius:4, height:28, padding:'0 12px', cursor:'pointer' },
  addRowBar: { background:'#293145', color:'#51A0FF', border:'1px solid #51A0FF', borderRadius:4, height:28, width:'100%', cursor:'pointer', marginTop:8, fontSize:16 },
  
  // Table styles for По времени / По метражу
  tableDivider: { display:'flex', alignItems:'center', gap:12, marginBottom:12 },
  dividerLine: { flex:1, height:1, background:'#2E3749' },
  dividerText: { fontSize:13, color:'#888', whiteSpace:'nowrap' as const },
  tableHeader: { display:'flex', gap:8, marginBottom:4 },
  tableHeaderCell: { fontSize:12, color:'#808080' },
  tableRow: { display:'flex', gap:8, marginBottom:4, alignItems:'center' },
  tableInput: { background:'#424F67', color:'#fff', border:'1px solid #2F3136', borderRadius:4, height:28, padding:'0 8px' },
  tableDeleteBtn: { background:'#2E3749', color:'#fff', border:'1px solid #2E3749', borderRadius:4, height:28, width:32, minWidth:32, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }
};

export default AddReportPage;
